import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { share } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  Invoice,
  InvoiceItems,
  ManualInvoice,
  SendDownloadInvoice,
  CreditPayment,
  SettingsInvoice,
  CustomerSettingsInvoice,
  CustomInvoiceField,
  CustomInvoiceFieldValue,
  ManualInvoicePartialPaySetting,
  NonCustomerModel,
  RecurringInvoiceCancellation,
  RecurringInvoiceTip,
  InvoiceAttachment,
  SendSMSInvoice,
  SendReceiptInvoice,
} from '../models/invoice.model';
import { InvoiceTemplateModel } from '../models/invoice-template.model';
import { Pagination } from 'src/app/shared/models/pagination.model';

@Injectable({
  providedIn: 'root',
})
export class InvoiceService {
  public baseRoute = '/portal/invoice/manual';

  private mockInvoices: BehaviorSubject<Invoice[]>;
  private mockInvoiceItems: BehaviorSubject<InvoiceItems[]>;
  private _reloadList = new Subject<boolean>();
  private _filterInvoice = new Subject<{source: string, searchText: string}>();

  constructor(private httpClient: HttpClient) {
    this.mockInvoices = new BehaviorSubject<Invoice[]>([
      {
        InvoiceNumber: '20191205000100001',
        Customer: 'Wolfgang Paganini',
        PaymentTerm: '24 Months',
        DueDate: '12th of the Month',
        BalanceDue: 2000,
        Status: 'Viewed',
      },
      {
        InvoiceNumber: '20191205000100002',
        Customer: 'Johannes Baethoven',
        PaymentTerm: 'Full Payment',
        DueDate: '11/21/2019',
        BalanceDue: 0.0,
        Status: 'Paid',
      },
      {
        InvoiceNumber: '20191205000100003',
        Customer: 'Ray Mendelbach',
        PaymentTerm: '6 Months',
        DueDate: '5th of the Month',
        BalanceDue: 1250,
        Status: 'Past Due',
      },
      {
        InvoiceNumber: '20191205000100004',
        Customer: 'Sam Dunn',
        PaymentTerm: '5 weeks',
        DueDate: '6th of the Month',
        BalanceDue: 600,
        Status: 'Sent',
      },
      {
        InvoiceNumber: '20191205000100005',
        Customer: 'Clay Morrow',
        PaymentTerm: '9 Months',
        DueDate: '1st of the Month',
        BalanceDue: 550,
        Status: 'Sent',
      },
    ]);

    this.mockInvoiceItems = new BehaviorSubject<InvoiceItems[]>([]);
  }

  setFilter(value: {source: string, searchText: string}): void {
    this._filterInvoice.next(value);
  }

  onFilterChange(): Observable<{source: string, searchText: string}> {
    return this._filterInvoice.pipe(share<{source: string, searchText: string}>());
  }

  reloadInvoiceList(): void {
    this._reloadList.next(true);
  }

  onReloadInvoiceList(): Observable<boolean> {
    return this._reloadList.pipe(share<boolean>());
  }

  // InvoiceNumber
  getInvoiceNumber<T>(terminalId: string): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/createinvoicenumber/${terminalId}`,
      null
    );
  }

  generateManualInvoiceNumber<T>(): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/generatemanualinvoicenumber`
    );
  }

  // Creater Invoice by OrderId
  createInvoice<T>(orderId: string, source: string): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/createinvoicebyorderid/${orderId}/${source}`,
      null
    );
  }
  // Creater Invoice by OrderId
  updateOrderInvoice<T>(orderId: string, source: string): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/updateinvoicebyorderid/${orderId}/${source}`,
      null
    );
  }

  saveInvoice<T>(
    orderId: string,
    source: string,
    invoiceNumber: string
  ): Observable<T> {
    if (invoiceNumber) {
      return this.httpClient.post<T>(
        `${environment.invoiceAPIRoot}/updateinvoicebyorderid/${orderId}/${source}`,
        null
      );
    } else {
      return this.httpClient.post<T>(
        `${environment.invoiceAPIRoot}/createinvoicebyorderid/${orderId}/${source}`,
        null
      );
    }
  }

  // save manual invoice
  saveManualInvoice<T>(payload: ManualInvoice): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/savemanualinvoice`,
      payload
    );
  }

  // save manual invoice
  saveManualInvoiceNoCustomer<T>(payload: ManualInvoice): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/savemanualinvoicewithoutcustomer`,
      payload
    );
  }

  // update manual invoice
  updateManualInvoice<T>(payload: ManualInvoice): Observable<T> {
    return this.httpClient.put<T>(
      `${environment.invoiceAPIRoot}/updatemanualinvoice`,
      payload
    );
  }

  getManualInvoices<T>(locationId: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getmanualinvoices?locationId=${locationId}`
    );
  }

  getPagedManualInvoices<T>(payload: Pagination): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/getpagedmanualinvoices?locationId=${payload.locationId}`,
      payload
    );
  }

  getManualInvoicePayments<T>(invoiceNumber: string): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getmanualinvoicepayments/${invoiceNumber}`
    );
  }

  getCustomerManualInvoicesByStatus<T>(
    customerId: number,
    sendFilter: string
  ): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getcustomermanualinvoicesbystatus?customerId=${customerId}&sendFilter=${sendFilter}`
    );
  }

  getManualInvoice(invoiceNumber: string): Observable<ManualInvoice> {
    return this.httpClient.get<ManualInvoice>(
      `${environment.invoiceAPIRoot}/getmanualinvoice/${invoiceNumber}`
    );
  }

  getLineItemsByInvoiceId<T>(invoiceNumber: string): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getlineitemsbyinvoiceid/${invoiceNumber}`
    );
  }

  getManualInvoiceDetailsByInvoiceNumber<T>(
    invoiceNumber: string
  ): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getmanualinvoicedetailsbyinvoicenumber/${invoiceNumber}`
    );
  }

  // PaymentTerms
  getPaymentTerms<T>(): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getpaymentterms`
    );
  }

  getRecurringInvoices<T>(): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getrecurringmanualinvoices`
    );
  }

  validateInvoiceNumber<T>(invoiceNumber: string): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/${invoiceNumber}/validate`
    );
  }

  deleteManualInvoiceDetail<T>(
    manualInvoiceDetailId: number,
    updatedBy: number
  ): Observable<T> {
    return this.httpClient.delete<T>(
      `${environment.invoiceAPIRoot}/detail/${manualInvoiceDetailId}/delete?updatedBy=${updatedBy}`
    );
  }

  addManualInvoiceDetail<T>(manualInvoiceDetail: any): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/addmanualinvoicedetail`,
      manualInvoiceDetail
    );
  }

  updateManualInvoiceDetail<T>(manualInvoiceDetail: any): Observable<T> {
    return this.httpClient.put<T>(
      `${environment.invoiceAPIRoot}/updatemanualinvoicedetail`,
      manualInvoiceDetail
    );
  }

  downloadInvoice(payload: SendDownloadInvoice) {
    return this.httpClient.post(
      `${environment.invoiceAPIRoot}/download`,
      payload,
      { responseType: 'arraybuffer' }
    );
  }

  sendInvoice<T>(payload: SendDownloadInvoice): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/send`,
      payload
    );
  }

  sendSMSInvoice<T>(payload: SendSMSInvoice): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/SendInvoiceToSMS`,
      payload
    );
  }

  sendmultipleinvoices<T>(payload: string[]): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/sendmultipleinvoices`,
      payload
    );
  }

  sendMultipleInvoicesByStatus<T>(
    customerId: number,
    status: string
  ): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/sendmultipleinvoicesbystatus/${customerId}/${status}`,
      null
    );
  }

  //MOCK DATA
  getMockInvoices<T>(): Observable<Invoice[]> {
    return this.mockInvoices.asObservable();
  }

  getMockInvoiceItems<T>(): Observable<InvoiceItems[]> {
    return this.mockInvoiceItems.asObservable();
  }

  addMockInvoiceItem(invoiceItem: InvoiceItems) {
    let items = this.mockInvoiceItems.getValue();
    items.push(invoiceItem);
    this.mockInvoiceItems.next(items);
  }

  removeMockInvoiceItem<T>(itemId: number) {
    let items = this.mockInvoiceItems.getValue();
    let filtered = items.filter((item) => {
      return item.ItemId !== itemId;
    });
    this.mockInvoiceItems.next(filtered);
  }

  clearAllMockInvoiceItems() {
    this.mockInvoiceItems.next([]);
  }

  getPaidInvoiceByInvoiceNumber<T>(invoiceId: string): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getpaidinvoicebyinvoicenumber/${invoiceId}`
    );
  }

  getPaidManualInvoiceByInvoiceNumber<T>(invoiceId: string): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getpaidmanualinvoicebyinvoicenumber/${invoiceId}`
    );
  }

  getSettingsInvoice<T>(): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getsettingsinvoice`
    );
  }

  savesettingsinvoice<T>(payload: SettingsInvoice): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/savesettingsinvoice`,
      payload
    );
  }

  saveCustomerSettingInvoice<T>(
    payload: CustomerSettingsInvoice
  ): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/savecustomersettinginvoice`,
      payload
    );
  }

  deletecustomersettinginvoice<T>(
    settingid: number,
    updatedBy: number
  ): Observable<T> {
    return this.httpClient.delete<T>(
      `${environment.invoiceAPIRoot}/deletecustomersettinginvoice/${settingid}/delete?updatedBy=${updatedBy}`
    );
  }

  getallsettingscustomerinvoice<T>(): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getallsettingscustomerinvoice`
    );
  }

  getallcustomerwithinvoicesetting<T>(): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getallcustomerwithinvoicesetting`
    );
  }

  getSettingsCustomerInvoice<T>(customerId: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getcustomersettinginvoice/${customerId}`
    );
  }

  deleteManualInvoice<T>(
    invoiceNumber: string,
    updatedBy: number | string
  ): Observable<T> {
    return this.httpClient.delete<T>(
      `${environment.invoiceAPIRoot}/deletemanualinvoice/${invoiceNumber}/${updatedBy}`
    );
  }

  getManualInvoiceDocuments(invoiceNumber: any): Observable<InvoiceAttachment> {
    return this.httpClient.get<InvoiceAttachment>(
      `${environment.invoiceAPIRoot}/getmanualinvoicedocumentbyinvoicenumber/${invoiceNumber}`
    );
  }

  deleteManualInvoiceDocument<T>(
    docId: number,
    updatedBy: number,
    invoiceNumber: string
  ): Observable<T> {
    return this.httpClient.delete<T>(
      `${environment.invoiceAPIRoot}/deletemanualinvoicedocument/${docId}/${updatedBy}/${invoiceNumber}`
    );
  }

  getInvoiceCustomFields<T>(): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getinvoicecustomfields`
    );
  }

  deleteInvoiceCustomField<T>(
    fieldId: number,
    updatedBy: number
  ): Observable<T> {
    return this.httpClient.delete<T>(
      `${environment.invoiceAPIRoot}/deleteinvoicecustomfield/${fieldId}/${updatedBy}`
    );
  }

  upsertInvoiceCustomField<T>(field: CustomInvoiceField): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/upsertinvoicecustomfield`,
      field
    );
  }

  checkExistingCusomtFields<T>(field: string): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/countinvoicecustomfield/${field}`
    );
  }

  saveNonCustomerInvoiceDetails<T>(payload: NonCustomerModel): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/createinvoicecustomer`,
      payload
    );
  }

  upsertretailinvoicecustomer<T>(
    invoiceNumber: string,
    customerId: number
  ): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/upsertretailinvoicecustomer/${invoiceNumber}/${customerId}`,
      null
    );
  }

  getAllCustomFieldValuesByInvoiceNumber<T>(
    invoiceNumber: string
  ): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getallcustomfieldvaluesbyinvoicenumber/${invoiceNumber}`
    );
  }

  upsertInvoiceCustomFieldValues<T>(
    fields: CustomInvoiceFieldValue[]
  ): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/upsertinvoicecustomfieldvalues`,
      fields
    );
  }

  getPartialPaySettingByInvoiceNumber<T>(invoiceNumber: string): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getpartialpaysettingbyinvoicenumber/${invoiceNumber}`
    );
  }

  upsertManualInvoicePartialPaySetting<T>(
    payload: ManualInvoicePartialPaySetting
  ): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/upsertmanualinvoicepartialpaysetting`,
      payload
    );
  }

  deleteManualInvoicePartialPaySetting<T>(
    invoiceNumber: string,
    updatedBy: number
  ): Observable<T> {
    return this.httpClient.delete<T>(
      `${environment.invoiceAPIRoot}/deletemanualinvoicepartialpaysetting/${invoiceNumber}/${updatedBy}`
    );
  }

  getInvoiceTemplates<T>(): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/invoicetemplates`
    );
  }

  upsertInvoiceTemplate<T>(payload: InvoiceTemplateModel): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/invoicetemplates`,
      payload
    );
  }

  deleteInvoiceTemplate<T>(templateId: number): Observable<T> {
    return this.httpClient.delete<T>(
      `${environment.invoiceAPIRoot}/invoicetemplates/delete/${templateId}`
    );
  }

  getInvoiceTemplate<T>(id: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/invoicetemplate/${id}`
    );
  }

  uploadInvoiceTemplateImage(payload: any) {
    return this.httpClient.post(
      environment.invoiceAPIRoot + '/invoicetemplates/upload-image',
      payload
    );
  }

  getInvoiceEmployees<T>(invoiceNumber: string): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/employee/getall/${invoiceNumber}`
    );
  }

  saveInvoiceEmployee(payload: any) {
    return this.httpClient.post(
      environment.invoiceAPIRoot + '/employee/save',
      payload
    );
  }

  deleteInvoiceEmployee(invoiceNumbe: string, employeeId: number) {
    return this.httpClient.delete(
      environment.invoiceAPIRoot +
        `/employee/delete/${invoiceNumbe}/${employeeId}`
    );
  }

  uploadCustomersig(payload: any) {
    return this.httpClient.post(
      environment.invoiceAPIRoot + '/uploadcustomersig',
      payload
    );
  }

  getCustomerSigByInvoiceNumber<T>(invoiceNumber: string): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getcustomersigbyinvoicenumber/${invoiceNumber}`
    );
  }

  upsertManualInvoiceAutoSendSchedule(payload: any) {
    return this.httpClient.post(
      environment.invoiceAPIRoot + '/upsertmanualinvoiceautosendschedule',
      payload
    );
  }

  getAllScheduledInvoices<T>(): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getallscheduledinvoices`
    );
  }

  deleteScheduledInvoice(id: number, employeeId: number) {
    return this.httpClient.delete(
      environment.invoiceAPIRoot + `/deletescheduledinvoice/${id}/${employeeId}`
    );
  }

  getInvoicesByInvoiceNumber<T>(
    invoiceId: string,
    withLinteItems: boolean
  ): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getinvoicebyinvoicenumber/${invoiceId}`
    );
  }

  getRetailInvoiceCustomer<T>(invoiceId: string): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getretailinvoicecustomer/${invoiceId}`
    );
  }

  getInvoiceLineItems<T>(invoiceNumber: string): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getmanualinvoicedetailsbyinvoicenumber/${invoiceNumber}`
    );
  }

  getActiveInvoiceTemplate<T>(): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/invoicetemplate/active`
    );
  }

  setRecurringInvoiceCardNumberByInvoiceNumber<T>(
    invoiceNumber: string,
    cardId: number
  ): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.recurringInvoiceAPIRoot}/setrecurringinvoicecardnumberbyinvoicenumber/${cardId}/${invoiceNumber}`,
      null
    );
  }

  setRecurringInvoiceTipConfiguration<T>(
    payload: RecurringInvoiceTip
  ): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.recurringInvoiceAPIRoot}/savetipdetails`,
      payload
    );
  }

  getRecurringInvoiceConfigurationByInvoiceNumber<T>(
    invoice: string
  ): Observable<T> {
    if (invoice.charAt(0) === 'R') {
      return this.httpClient.get<T>(
        `${environment.recurringInvoiceAPIRoot}/getconfigurationbychildinvoicenumber/${invoice}`
      );
    }

    return this.httpClient.get<T>(
      `${environment.recurringInvoiceAPIRoot}/getconfigurationbyinvoicenumber/${invoice}`
    );
  }

  getRecurringInvoiceById<T>(id: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.recurringInvoiceAPIRoot}/getrecurringinvoiceid/${id}`
    );
  }

  getRecurringInvoiceCancellationReasons<T>(): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.recurringInvoiceAPIRoot}/getcancellationreasons`
    );
  }

  getRecurringInvoicesByRecurringId<T>(id: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.recurringInvoiceAPIRoot}/getinvoicesbyrecurringinvoiceid/${id}`
    );
  }

  upsertRecurringInvoiceCancellation<T>(
    payload: RecurringInvoiceCancellation
  ): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.recurringInvoiceAPIRoot}/upsertrecurringinvoicecancellation`,
      payload
    );
  }

  getManualInvoicesByInvoiceNumbers<T>(
    invoiceNumbers: string[]
  ): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/getmanualinvoicesbyinvoicenumbers`,
      invoiceNumbers
    );
  }

  markAsViewed<T>(invoiceNumber: any): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/markasviewed/${invoiceNumber}`,
      null
    );
  }

  getTipByInvoiceNumber<T>(invoiceNumber: string): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.tipAPIRoot}/getbyinvoicenumber/${invoiceNumber}`
    );
  }

  updateInvoiceTip<T>(payload: any): Observable<T> {
    return this.httpClient.put<T>(
      `${environment.invoiceAPIRoot}/updatetip`,
      payload
    );
  }

  getAllInvoiceNotesByInvoiceNumber<T>(invoiceNumber: string): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getallinvoicenote/${invoiceNumber}`
    );
  }

  getInvoiceNote<T>(noteId: number): Observable<T> {
    return this.httpClient.get<T>(
      `${environment.invoiceAPIRoot}/getinvoicenotebyid/${noteId}`
    );
  }

  saveInvoiceNote<T>(payload: any): Observable<T> {
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/insertinvoicenote`,
      payload
    );
  }

  updateInvoiceNote<T>(payload: any): Observable<T> {
    return this.httpClient.put<T>(
      `${environment.invoiceAPIRoot}/updateinvoicenote`,
      payload
    );
  }

  deleteInvoiceNote<T>(noteId: number): Observable<T> {
    return this.httpClient.delete<T>(
      `${environment.invoiceAPIRoot}/deleteInvoiceNote/${noteId}`
    );
  }

  sendReceipt<T>(payload: SendReceiptInvoice): Observable<T>{
    return this.httpClient.post<T>(
      `${environment.invoiceAPIRoot}/sendreceipt`,
      payload
    );
  }
}
